import { useCallback, useEffect } from "react";

import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from "@chakra-ui/react";

import { useFormContext, useWatch } from "react-hook-form";
import { FiSend } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { create, enforce, test } from "vest";

import { Id } from "@/jenesys-types/data/common";
import { CreateQuerySchema } from "@/jenesys-types/data/communication";
import { DataType } from "@/jenesys-types/data/enums";
import { BankStatement } from "@/jenesys-types/jenesys-entities/config";

import { Form } from "@/components/forms/Form/Form";
import { FormInput } from "@/components/forms/FormBaseInput/FormBaseInput";
import { FormCheckbox } from "@/components/forms/FormCheckbox/FormCheckbox";
import { FormField } from "@/components/forms/FormField/FormField";
import { FormSelect } from "@/components/forms/FormSelect/FormSelect";
import { FormTextarea } from "@/components/forms/FormTextarea/FormTextarea";
import { useCommunicationQueries } from "@/hooks/features/useCommunicationQueries";
import { useCompanyMembers } from "@/hooks/features/useCompanyMembers";
import useActionTrigger from "@/hooks/util/useActionTrigger";
import { useCompany } from "@/hooks/util/useCompany";
import { selectClients } from "@/store/features/companies/companies";
import { loadCompanyMembers } from "@/store/features/companyMembers/companyMembers";
import { toastError, toastSuccess } from "@/util/helpers";
// react-hook-form imported above
import { getAllTemplates } from "@/utils/queryTemplates";
import {
  buildTemplateContextFromEntry,
  substituteTemplatePlaceholders
} from "@/utils/templateSubstitution";

interface FormData {
  client: string;
  assignTo: string;
  cc: string;
  subject: string;
  message: string;
  email_notifications_enabled: boolean;
  templateId?: string | null;
}

// Validation schema using Vest
const NewQueryValidationSchema = create((data: FormData) => {
  test(
    "assignTo",
    () => {
      enforce(data.assignTo).isNotNull();
      enforce(data.assignTo).isNotEmpty();
    },
    "Please select an assignee"
  );
  test(
    "subject",
    () => {
      enforce(data.subject).isNotNull();
      enforce(data.subject).isNotEmpty();
    },
    "Subject is required"
  );
  test(
    "message",
    () => {
      enforce(data.message).isNotNull();
      enforce(data.message).isNotEmpty();
    },
    "Message is required"
  );
});

export const NewQueryModal = ({
  isOpen,
  onClose,
  context,
  statementId,
  statement
}: {
  isOpen: boolean;
  onClose: () => void;
  context: "company" | "client";
  statementId?: Id;
  statement?: BankStatement;
}) => {
  const { createNewQuery, isCreating, dispatchLoadQueries } =
    useCommunicationQueries();

  const clients = useSelector(selectClients);
  const { activeClientId } = useCompany();
  const { companyMembers } = useCompanyMembers();
  const dispatch = useDispatch();

  const initialValues: FormData = {
    client: "",
    assignTo: "",
    cc: "",
    subject: "",
    message: "",
    email_notifications_enabled: false,
    templateId: null
  };

  // Load company members when active query changes
  useEffect(() => {
    if (activeClientId) {
      dispatch(loadCompanyMembers(activeClientId));
    }
  }, [dispatch, activeClientId]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const { trigger: triggerNotify } = useActionTrigger({
    actionName: "notify",
    dataType: DataType.CommunicationsQuery,
    onSuccess: () => toastSuccess("Notification sent"),
    onError: (error) => toastError("Failed to send notification", error)
  });

  const handleSubmit = (data: FormData) => {
    const queryData: CreateQuerySchema = {
      title: data.subject,
      description: data.message,
      company_id: context === "company" ? data.client : activeClientId || "",
      status: "OPEN",
      assigned_to_id: data.assignTo,
      linked_bank_transaction_id: statementId,
      type: "GENERAL",
      email_notifications_enabled: !!data.email_notifications_enabled
    };

    createNewQuery(queryData, (newQueryId: string) => {
      if (queryData.email_notifications_enabled) {
        triggerNotify({
          pathParams: { companyId: queryData.company_id },
          data: { queries_ids: [newQueryId] }
        });
      }
      handleClose();
      dispatchLoadQueries();
    });
  };

  const clientOptions =
    clients?.items.map((client) => ({
      label: client.name,
      value: client.id
    })) || [];

  const memberOptions = companyMembers.map((member) => ({
    label: `${member.first_name} ${member.last_name}`,
    secondaryLabel: member.email,
    value: member.id
  }));

  const templateOptions = getAllTemplates().map((t) => ({
    label: t.title,
    value: t.templateId,
    secondaryLabel: t.subjectText
  }));

  const TemplateWatcherGeneral = () => {
    const { setValue } = useFormContext();
    const templateId = useWatch({ name: "templateId" }) as string | null;
    const assignToId = useWatch({ name: "assignTo" }) as string | null;

    useEffect(() => {
      if (!templateId) return;
      const t = getAllTemplates().find((x) => x.templateId === templateId);
      if (!t) return;

      // Build context from bank statement when available
      const ctx = buildTemplateContextFromEntry({
        date: statement?.date || null,
        amount: statement?.amount ?? null,
        currency: statement?.currency ?? null,
        description: statement?.description || statement?.supplier || null,
        reference: statement?.reference || null
      });
      const subject = substituteTemplatePlaceholders(t.subjectText, ctx);
      const body = substituteTemplatePlaceholders(t.bodyText, ctx);
      setValue("subject", subject, { shouldDirty: true, shouldValidate: true });
      setValue("message", body, { shouldDirty: true, shouldValidate: true });
    }, [templateId, assignToId, setValue]);

    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New General Query</ModalHeader>
        <ModalCloseButton />
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={NewQueryValidationSchema}
          mode="onSubmit"
        >
          <ModalBody>
            <Box mb={4}>
              <FormField
                name="templateId"
                label="Select a Template (Optional)"
                control={FormSelect}
                isClearable
                options={templateOptions}
                placeholder="No template"
                isSearchable
              />
            </Box>
            <TemplateWatcherGeneral />
            {context === "company" && (
              <Box mb={4} width="50%">
                <FormField
                  name="client"
                  label="Client:"
                  control={FormSelect}
                  options={clientOptions}
                  placeholder="Select client"
                  isSearchable
                />
              </Box>
            )}
            <HStack spacing={4} mb={4}>
              <Box width="50%">
                <FormField
                  name="assignTo"
                  label="Assign To: "
                  control={FormSelect}
                  options={memberOptions}
                  searchMatchFrom="any"
                  placeholder="Select assignee"
                  isRequired
                  isSearchable
                />
              </Box>

              <Box width="50%">
                <FormField
                  name="cc"
                  label="CC:"
                  control={FormSelect}
                  options={memberOptions}
                  searchMatchFrom="any"
                  placeholder="Select cc"
                  isSearchable
                />
              </Box>
            </HStack>

            <Box mb={4}>
              <FormField
                name="subject"
                label="Subject: "
                control={FormInput}
                placeholder="Type your subject here..."
                isRequired
              />
            </Box>

            <FormField
              name="message"
              label="Message: "
              control={FormTextarea}
              placeholder="Type your message here..."
              isRequired
              rows={6}
            />
            <Box mt={4} mb={0}>
              <FormField
                name="email_notifications_enabled"
                label=""
                control={FormCheckbox}
                helpText="I would like to send an email as well as notify the assignee."
                sideBySide
              />
            </Box>
          </ModalBody>

          <ModalFooter justifyContent="space-between">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              rightIcon={<FiSend />}
              type="submit"
              isLoading={isCreating}
              loadingText="Sending..."
            >
              Send
            </Button>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
